import { apiConnection } from "@/services/api-connection";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { storageManager } from "@/services/storage/storage-manager";
import { userManager } from "@/services/security/user-manager";
import { useUserStore } from "@/store/user/user";
import router from "@/router";
import { cookieManager } from "@/services/cookie/cookie-manager";
import { registrationConstante } from "@/constantes/registration";
import * as Sentry from "@sentry/vue";

const URL_SEND_NEW_PASSWORD = "/app/user/reset-password";
const URL_RESET_PASSWORD = "/app/user/ask-reset-password";

// Délai au-delà duquel _performRefresh() abandonne l'attente du fetch de refresh et règle sa
// promesse malgré tout (cf. commentaire dans _performRefresh). Fixé large (30s, très au-delà
// d'un aller-retour réseau normal même dégradé) car apiConnection ne configure aucun timeout
// axios : ce délai n'est qu'un filet de sécurité contre une promesse qui ne se réglerait jamais
// (ERR_NETWORK, ECONNABORTED, ERR_CANCELED, cf. errorManager.dispatchError), pas un timeout de
// fonctionnement normal — un refresh lent sur mobile/VPN doit avoir le temps d'aboutir.
export const REFRESH_TIMEOUT_MS = 30000;

// Lecture seule du payload JWT (jamais de vérification de signature côté client : le serveur l'a
// déjà signé, le rôle ici est uniquement d'en lire une revendication informative). Ne doit jamais
// throw — un token malformé doit dégrader vers "pas d'info exploitable", pas casser l'appelant.
function decodeJwtPayload(token) {
  const base64Url = token?.split(".")?.[1];
  if (!base64Url) {
    return null;
  }

  try {
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

class LoginManager {
  token = "";
  tokenExpirationDate = null;
  stayConnected = 0;
  apiPath = "";
  authorizedLoginAttempts = 5;
  // Verrou "un seul refresh en vol". Avec la rotation single_use côté serveur, un refresh
  // token présenté est immédiatement supprimé et remplacé : sans ce verrou, deux 401
  // concurrents déclencheraient chacun un appel réseau, le second présenterait un refresh
  // token déjà consommé par le premier et échouerait, provoquant une déconnexion
  // intempestive alors que la session était valide.
  refreshPromise = null;

  constructor() {
    this.reloadToken();
  }

  setApiPath(platform) {
    const url = "https://" + platform.hostname + "/api";
    const date = new Date();
    window.localStorage.setItem("studea-apiPathDate", date.toISOString());
    window.localStorage.setItem("studea-apiPath", url);
    window.sessionStorage.setItem("studea-apiPath", url);
    localStorage.setItem("studea-activateChatbot", platform.activateChatBot ? 1 : 0);
    this.apiPath = url;
  }

  getApiPath() {
    return window.localStorage.getItem("studea-apiPath")
      ? window.localStorage.getItem("studea-apiPath")
      : window.sessionStorage.getItem("studea-apiPath");
  }

  clearApiPath() {
    window.localStorage.removeItem("studea-apiPath");
    window.localStorage.removeItem("studea-apiPathDate");
    window.sessionStorage.removeItem("studea-apiPath");
    window.localStorage.removeItem("studea-activateChatbot");
    this.apiPath = "";
  }

  reloadToken() {
    this.token = this.getToken();
    this.stayConnected = this.getStayConnected();
    const localApiPath = this.getApiPath();
    if (typeof localApiPath == "string") {
      this.apiPath = localApiPath;
    }
  }

  // `Storage.setItem` stringifie toute valeur : sans ce garde, une valeur absente (ex.
  // `expires` lors d'un échange de jeton SSO) écrirait "undefined"/"null", relue ensuite
  // comme truthy par reloadToken().
  _setOrRemove(storage, key, value) {
    if (value === null || value === undefined) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, value);
    }
  }

  // Le refresh token n'est volontairement jamais stocké ni conservé en mémoire (ST143-TI-18073) :
  // il est détenu par le navigateur sous forme de cookie HttpOnly, hors de portée de JS. Seul
  // l'access token (TTL 5 min) reste en storage.
  setToken(res, apiPath, stayConnected = false, preserveStayConnected = false) {
    this.token = res.token;
    this.tokenExpirationDate = res.expires;

    if (localStorage.getItem("studea-newTab") && localStorage.getItem("studea-newTab") === "1") {
      storageManager.setFromLocalStorage();
    } else {
      if (stayConnected) {
        this._setOrRemove(window.localStorage, "studea-token", res.token);
        this._setOrRemove(window.localStorage, "studea-tokenExpirationDate", res.expires);
        window.localStorage.setItem("studea-apiPath", apiPath);
        window.localStorage.setItem("studea-stayConnected", "1");
      } else if (!preserveStayConnected) {
        // Reflète l'intention de la session courante : un résidu "1" laissé par une ancienne
        // connexion "Rester connecté" ferait mentir getStayConnected() pour cette session (elle
        // désactiverait le logout d'inactivité, cf. user-timeout-manager.js) et serait réinjecté
        // dans localStorage au premier refresh (cf. _performRefresh) sans consentement de
        // l'utilisateur. Seule une usurpation en cours (SwitchView) passe preserveStayConnected :
        // le consentement appartient alors au compte d'origine, que restoreUser() rétablit une
        // fois l'usurpation terminée. Cette intention ne se déduit pas du storage : le flag
        // studea-switched est collant et survit à un échange de jeton échoué.
        window.localStorage.removeItem("studea-stayConnected");
      }
      this._setOrRemove(window.sessionStorage, "studea-token", res.token);
      this._setOrRemove(window.sessionStorage, "studea-tokenExpirationDate", res.expires);
      window.sessionStorage.setItem("studea-apiPath", apiPath);
      window.sessionStorage.setItem("studea-sessionTS", Date.now());
      this._syncSwitchedFromToken(res.token);
    }
  }

  // La bannière "Vous incarnez" et son icône de désincarnation (HeaderComponent) lisent
  // `studea-switched`/`studea-switchedFrom` depuis sessionStorage, qui ne survivent pas à un
  // nouvel onglet ouvert autrement que par le relais prepareNewTab (clic droit / target=_blank,
  // cf. storageManager) — celui-là recopie déjà toute la sessionStorage, y compris ces clés, donc
  // n'est pas concerné ici (branche `if` ci-dessus). Un onglet réellement neuf s'authentifie
  // désormais seul via le cookie HttpOnly (POST /token/refresh, ST143-TI-18073) sans jamais passer
  // par ce relais : sans cette synchronisation, la bannière et l'icône disparaissaient alors que
  // l'usurpation restait bien active côté serveur (le JWT réémis porte toujours la revendication —
  // ST143-TI-13869/ST143-TI-18073 §11). On dérive donc ces flags du token à chaque écriture
  // "normale", plutôt que de dépendre uniquement du moment initial du switch.
  _syncSwitchedFromToken(token) {
    const payload = decodeJwtPayload(token);
    const hops = Array.isArray(payload?.impersonators)
      ? payload.impersonators
      : payload?.impersonator?.username
        ? [payload.impersonator]
        : [];

    if (hops.length === 0) {
      window.sessionStorage.removeItem("studea-switched");
      window.sessionStorage.removeItem("studea-switchedFrom");
      return;
    }

    window.sessionStorage.setItem("studea-switched", "1");
    // Dernier maillon = le plus récent (App\Service\Security\ImpersonationContext::toClaim(),
    // "outermost first") : c'est lui qui a produit l'identité actuellement incarnée, donc celui
    // pertinent pour savoir si l'icône de désincarnation (switch initié depuis le front, type
    // studea_admin/studea_manager/studea legacy) a un sens, par opposition à une usurpation
    // initiée depuis le back-office Zama (type zama), qu'on ne peut pas désincarner depuis ici.
    const innermostType = hops[hops.length - 1]?.type ?? "";
    window.sessionStorage.setItem("studea-switchedFrom", innermostType.startsWith("studea") ? "front" : "zama");
  }

  _readSwitchedStorage(key) {
    if (window.sessionStorage.getItem("studea-switched") && window.sessionStorage.getItem("studea-switched") === "1") {
      return window.sessionStorage.getItem(key);
    }
    return window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
  }

  getTokenExpirationDate() {
    return this._readSwitchedStorage("studea-tokenExpirationDate");
  }

  tokenIsValid() {
    const current = new Date();
    const storedDate = this.getTokenExpirationDate();
    let expirationDate = null;
    if (storedDate) {
      expirationDate = new Date(storedDate);
    }

    return expirationDate && expirationDate.getTime() > current.getTime();
  }

  isToken() {
    return this.getToken();
  }

  getToken() {
    return this._readSwitchedStorage("studea-token");
  }

  getStayConnected() {
    return parseInt(window.localStorage.getItem("studea-stayConnected"));
  }

  // Point d'entrée public : un seul refresh réseau à la fois. Tout appelant concurrent
  // reçoit la même promesse en cours plutôt que de déclencher un second appel réseau
  // (cf. commentaire sur refreshPromise).
  refreshConnection() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const { result, networkSettled } = this._performRefresh();
    this.refreshPromise = result;

    // Le verrou n'est libéré qu'une fois l'appel réseau sous-jacent réellement terminé, pas dès
    // que `result` est réglée : le timeout de secours ci-dessous règle `result` sans attendre la
    // fin du fetch, qui continue de tourner en arrière-plan. Libérer le verrou à ce moment-là
    // permettrait à un nouvel appel refreshConnection() de démarrer pendant que l'ancien est
    // toujours en vol, recréant la race single_use que ce verrou est censé éviter. Si le fetch
    // ne se règle jamais (ERR_NETWORK/ECONNABORTED/ERR_CANCELED, cf. _performRefresh), le verrou
    // reste tenu indéfiniment : c'est un compromis assumé — on ne peut pas savoir si cet appel
    // finira par atteindre le serveur, donc on préfère bloquer les refresh suivants (ils
    // récupèrent alors la même promesse déjà réglée) plutôt que risquer un doublon.
    networkSettled.finally(() => {
      if (this.refreshPromise === result) {
        this.refreshPromise = null;
      }
    });

    return result;
  }

  // Contrat : resolve({ success: true }) en cas de succès, resolve(false) sur tout échec.
  // Chaque branche doit régler `result` — un simple `return` la laisserait pending et
  // bloquerait indéfiniment les appelants qui l'attendent (écran blanc au démarrage).
  // On résout plutôt qu'on rejette car aucun appelant n'a de `.catch()` : un rejet
  // remplacerait le blocage par un unhandled rejection. Les appelants testent la
  // valeur retournée (`if (success)`), le contrat reste lisible côté consommateur.
  //
  // `networkSettled` est un signal distinct de `result`, utilisé uniquement par
  // refreshConnection() pour savoir quand libérer le verrou single-flight (cf. commentaire
  // ci-dessus) : il ne se règle qu'une fois l'appel réseau réellement terminé, jamais via le
  // timeout de secours.
  _performRefresh() {
    this.reloadToken();
    const magicLink = apiConnection.getMagicLinkToken();

    if (this.tokenIsValid()) {
      return { result: Promise.resolve({ success: true }), networkSettled: Promise.resolve() };
    }

    // Le refresh token est exclusivement porté par le cookie HttpOnly (ST143-TI-18073) : le corps
    // est donc toujours vide. Ne jamais y remettre une clé `refresh_token` — côté serveur
    // l'extracteur de corps est prioritaire sur celui de cookie, une valeur vide ou null
    // masquerait le cookie et casserait tout rafraîchissement.
    const credentials = {};

    // Un access token en storage est la seule trace exploitable d'une session passée. Son absence
    // distingue un accès anonyme (échec attendu, à traiter en silence) d'une vraie perte de
    // session (à notifier).
    const hadSession = Boolean(this.getToken());

    // Magic link sans session préalable : la session est portée par le lien, aucun cookie de
    // refresh n'a été émis — rien à rafraîchir.
    if (!hadSession && magicLink) {
      return { result: Promise.resolve(false), networkSettled: Promise.resolve() };
    }

    let settled = false;
    let timeoutId;
    let resolveResult;
    const result = new Promise((resolve) => {
      resolveResult = resolve;
    });
    let resolveNetworkSettled;
    const networkSettled = new Promise((resolve) => {
      resolveNetworkSettled = resolve;
    });

    // `settled` + le clearTimeout ici garantissent qu'un seul des trois déclencheurs
    // (succès, échec, timeout) règle effectivement `result` : les deux premiers annulent le
    // timeout avant qu'il ne puisse se déclencher, et les guards `if (settled)` dans
    // .then/.catch empêchent d'appliquer leurs effets de bord (setToken, logout) si le
    // timeout les a précédés.
    const settleOnce = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      resolveResult(value);
    };
    // Filet de sécurité : certaines erreurs axios (ERR_NETWORK, ECONNABORTED, ERR_CANCELED)
    // ne règlent jamais la promesse renvoyée par apiConnection.fetchApi (cf.
    // errorManager.dispatchError, qui fait un simple `return` sans resolve/reject dans ces
    // cas). Sans ce timeout, `result` — et donc le verrou refreshPromise — resterait bloquée
    // indéfiniment (écran blanc : LayoutView attend ce refresh avec appLoading=true). Fixé
    // large (cf. REFRESH_TIMEOUT_MS) car il ne doit se déclencher que sur une promesse qui ne
    // se réglera jamais, pas sur un refresh simplement lent.
    timeoutId = setTimeout(() => settleOnce(false), REFRESH_TIMEOUT_MS);

    apiConnection
      .fetchApi("post", this.apiPath + "/token/refresh", credentials, false)
      .then((data) => {
        // Réponse arrivée après l'expiration du timeout : `result` est déjà réglée (false) et
        // l'appelant a potentiellement déjà réagi en conséquence (déconnexion, etc.) —
        // appliquer le token maintenant laisserait l'état incohérent. On règle tout de même
        // `networkSettled` en premier : c'est ce qui libère le verrou pour les refresh
        // suivants, indépendamment de `result`.
        if (settled) {
          resolveNetworkSettled();
          return;
        }
        if (data.token) {
          this.setToken(data, this.apiPath, parseInt(window.localStorage.getItem("studea-stayConnected")));
          resolveNetworkSettled();
          settleOnce({ success: true });
        } else {
          resolveNetworkSettled();
          settleOnce(false);
        }
      })
      .catch((error) => {
        if (settled) {
          resolveNetworkSettled();
          return;
        }
        // Priorité au statut HTTP normalisé : un corps 503 objet peut exposer son propre
        // champ `code` métier (cf. contrat documenté en tête d'ErrorManager), qui ne doit
        // jamais masquer le vrai statut de la réponse.
        const status = error?.status ?? error?.code ?? error?.response?.status;
        // Maintenance : errorManager a déjà routé vers la page dédiée, on ne déconnecte pas.
        // Plateforme supprimée : errorManager a déjà déconnecté sans bruit.
        if (status === 503 || error?.platformRemoved) {
          resolveNetworkSettled();
          settleOnce(false);
          return;
        }
        // Aucune session préalable : cas normal d'une URL protégée ouverte sans être connecté,
        // pas d'une session perdue. On déconnecte sans bruit — ni Sentry ni notification, sinon
        // tout accès anonyme à une route protégée produirait une alerte mensongère et du bruit
        // d'observabilité.
        if (!hadSession) {
          userManager.logout(true);
          resolveNetworkSettled();
          settleOnce(false);
          return;
        }
        Sentry.captureException(error);
        notificationManager.showNotification("warning", i18n.global.t("error.message.session_lost"));
        userManager.logout(true);
        resolveNetworkSettled();
        settleOnce(false);
      });

    return { result, networkSettled };
  }

  login(login, password, stayConnected = false) {
    const credentials = {
      username: login,
      password: password,
    };
    if (stayConnected) {
      credentials.stayConnected = true;
    }

    return new Promise((resolve) => {
      const attempts = parseInt(this.loginAttempts());
      if (attempts && attempts >= this.authorizedLoginAttempts) {
        resolve({ success: false, message: i18n.global.t("error.login_blocked"), messageType: "warning" });
      } else {
        apiConnection
          .fetchApi("post", this.apiPath + "/login_check", credentials, false)
          .then((data) => {
            if (data.token) {
              cookieManager.deleteCookie("studea-loginAttempts");
              this.setToken(data, this.apiPath, stayConnected);
              resolve({ success: true, token: data.token });
            }
          })
          .catch((res) => {
            // Plateforme en maintenance : l'échec ne vient pas des identifiants, ne pas
            // incrémenter le compteur de tentatives (qui finirait par bloquer le compte).
            if (503 === res?.status) {
              resolve({ success: false, maintenance: true });
              return;
            }
            // Échec non imputable au mot de passe soumis (rate limiting, erreur serveur) :
            // ni compteur ni message écrasé, cf. _isInvalidCredentials.
            if (!this._isInvalidCredentials(res)) {
              resolve(this._normalizeLoginFailure(res));
              return;
            }
            this.invalidCredentials();
            if (attempts && attempts === this.authorizedLoginAttempts - 1) {
              res.message = i18n.global.t("error.login_blocked");
              res.messageType = "warning";
            }
            resolve(res);
          });
      }
    });
  }

  signIn(params, locale) {
    return new Promise((resolve) => {
      apiConnection
        .fetchApi("post", this.apiPath + "/" + locale + "/app/user/post/sign-in", params, false)
        .then((data) => {
          if (data.token) {
            this.setToken(data, this.apiPath);
            resolve({ success: true, token: data.token });
          }
        })
        .catch((res) => {
          // Plateforme en maintenance : l'échec ne vient pas des identifiants, l'écran
          // dédié est déjà affiché — ne pas résoudre un message/code vide.
          if (503 === res?.status) {
            resolve({ success: false, maintenance: true });
            return;
          }
          resolve({ success: false, message: res.message, code: res.code });
        });
    });
  }

  resetPassword(username) {
    return apiConnection.post(URL_RESET_PASSWORD, { username: username }, false).then(() => {
      notificationManager.showNotification(
        "success",
        i18n.global.t("reset_password_confirm"),
        10000,
        "top-end",
        false,
        "-no-header",
      );
    });
  }

  getResetHashStatus(hash) {
    return apiConnection.get(URL_SEND_NEW_PASSWORD + "/" + hash + "/status", {}, false);
  }

  renewResetLink(hash) {
    return apiConnection.post(URL_SEND_NEW_PASSWORD + "/" + hash + "/renew", {}, false);
  }

  async newPassword(password, hash, router) {
    const data = await apiConnection.post(URL_SEND_NEW_PASSWORD + "/" + hash, { password: password }, false);
    if (!data) {
      return;
    }

    notificationManager.showNotification(
      "success",
      i18n.global.t("new_password_confirm"),
      10000,
      "top-end",
      false,
      "-no-header",
    );
    router.push({ name: "Login" });
  }

  storeInitialToken() {
    const initialToken = window.localStorage.getItem("studea-token")
      ? window.localStorage.getItem("studea-token")
      : window.sessionStorage.getItem("studea-token");
    const initialTokenExpirationDate = window.localStorage.getItem("studea-tokenExpirationDate")
      ? window.localStorage.getItem("studea-tokenExpirationDate")
      : window.sessionStorage.getItem("studea-tokenExpirationDate");

    const initialOldTrainingCourse = window.localStorage.getItem("studea-oldTrainingCourse");

    const initialOldTriad = window.localStorage.getItem("studea-oldTriad");

    window.sessionStorage.setItem("studea-initial-token", initialToken);
    if (initialOldTrainingCourse) {
      window.sessionStorage.setItem("studea-initial-oldTrainingCourse", initialOldTrainingCourse);
    }
    if (initialOldTriad) {
      window.sessionStorage.setItem("studea-initial-oldTriad", initialOldTriad);
    }
    if (initialTokenExpirationDate) {
      window.sessionStorage.setItem("studea-initial-tokenExpirationDate", initialTokenExpirationDate);
    }

    window.sessionStorage.removeItem("studea-token");
    window.sessionStorage.removeItem("studea-tokenExpirationDate");
    // Purge héritée : plus écrite depuis ST143-TI-18073, mais un utilisateur déjà connecté au
    // moment du déploiement en a encore une copie vivante sur disque.
    window.sessionStorage.removeItem("studea-refreshToken");
  }

  redirect(triadId = null) {
    const urlParams = new URLSearchParams(window.location.search);
    const referer = urlParams.get("referer");

    if (referer) {
      router.push(referer);
    } else {
      const user = useUserStore().user;
      if (triadId || (user.target === "triad" && user.targetParams?.triadId)) {
        localStorage.setItem("studea-oldTriad", triadId ? triadId : user.targetParams.triadId);
      }

      if (triadId) {
        router.push({ name: "Dashboard" });
      } else {
        switch (user.target) {
          default:
          case "training":
            router.push({ name: "TrainingCourseList" });
            break;
          case "admin":
            router.push({ name: "adminHome" });
            break;
          case "triad":
            router.push({ name: "Dashboard" });
            break;
          case "registration":
            this.getRegistrationRedirection(user.targetParams);
            break;
        }
      }
    }
  }

  getRegistrationRedirection(params, currentRouter = router) {
    const actorType = params.registrationActorType;
    const registrationCryptedId = params.registrationCryptedId;
    const groupCryptedId = params.groupCryptedId;

    if (registrationCryptedId && groupCryptedId) {
      currentRouter.push({
        name: "TrainingPreRegistration",
        params: { registration: registrationCryptedId, group: groupCryptedId, tab: 0 },
      });
      return;
    }

    switch (actorType) {
      case registrationConstante.REGISTRATION_ACTOR_UFA:
        currentRouter.push({ name: "TrainingList" });
        break;
      case registrationConstante.REGISTRATION_ACTOR_COMPANY:
        currentRouter.push({ name: "CompanyContractList" });
        break;
      case registrationConstante.REGISTRATION_ACTOR_APPRENTICE:
      default:
        currentRouter.push({ name: "ApprenticeContractList" });
        break;
    }
  }

  // Un échec de /login_check ne vaut « mauvais mot de passe » que si l'API a réellement rejeté
  // les identifiants. Le rate limiting 429 (ThrottledAuthenticationFailureHandler) et les erreurs
  // serveur arrivent dans le même `.catch()` alors que le mot de passe soumis peut être correct :
  // les compter finirait par écraser le vrai message explicatif par error.login_blocked, en plus
  // du blocage déjà appliqué côté serveur (ST143-TI-18069).
  //
  // Le corps rejeté par errorManager n'expose de statut HTTP normalisé que pour la maintenance
  // (cf. contrat en tête d'ErrorManager) : le discriminant est donc `code`, que Lexik alimente
  // avec l'écho numérique du statut (`{code: 401, message}`).
  //
  // Limite connue : un refus de statut de compte (compte non activé, cf. UserChecker back) passe
  // par la même CustomUserMessageAccountStatusException, dont le code n'est pas mappable en
  // statut — Lexik renvoie donc lui aussi `{code: 401, message}`. Le front ne peut pas l'écarter
  // tant que le back n'expose pas de discriminant dédié ; ce cas reste compté.
  _isInvalidCredentials(res) {
    return 401 === res?.code;
  }

  // Le 429 est désormais normalisé par ErrorManager (message déjà présent) : le fallback
  // `res?.error` ici couvre les autres statuts non routés par ErrorManager (qui peuvent encore
  // arriver en `{error}`) ainsi que les rejets vides. `...res` est spread avant `success: false`
  // pour ne jamais laisser un `success: true` porté par le corps écraser le résultat : ce
  // `.catch()` ne peut être atteint que sur un échec de /login_check.
  _normalizeLoginFailure(res) {
    return {
      ...res,
      success: false,
      message: res?.message ?? res?.error ?? i18n.global.t("error.error_occured"),
    };
  }

  invalidCredentials() {
    let attempts = this.loginAttempts();
    if (!attempts) {
      attempts = 0;
    }
    attempts++;
    if (attempts <= this.authorizedLoginAttempts) {
      const expirationDate = new Date();
      expirationDate.setMinutes(expirationDate.getMinutes() + this.authorizedLoginAttempts);
      cookieManager.setCookie("studea-loginAttempts", attempts, {
        sameSite: "Strict",
        expires: expirationDate,
      });
    }
  }

  loginAttempts() {
    return cookieManager.getCookie("studea-loginAttempts");
  }

  getForm(type, actorType) {
    return apiConnection.get("/app/user/form/" + type, actorType).then((response) => response);
  }
}

export const loginManager = new LoginManager();
